import { useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';

interface TooltipProps {
  content: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
  children: ReactNode;
  className?: string;
}

/**
 * Tooltip — BRANDBOOK §6.11 (Sprint 28c hed_v3, O19C).
 *
 * Ink bg + krem text + radius 4. Delay 500ms (hover). Max-width 240px.
 * `aria-describedby` semantik.
 *
 * Portal (founder 2026-06-16): popover document.body'ye render edilir + position:fixed.
 * Eskiden trigger içinde position:absolute idi → tablo/kart `overflow-hidden` /
 * `overflow-x-auto` container'ları tooltip'i KIRPIYORDU (z-index çözmez; "grid arkasında
 * kalıyor" bug'ı). Portal + fixed, tüm overflow + stacking context'lerden kaçar.
 */
export function Tooltip({ content, side = 'top', delay = 500, children, className }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const id = useId();

  const computeCoords = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const gap = 6;
    switch (side) {
      case 'top':
        setCoords({ top: r.top - gap, left: r.left + r.width / 2 });
        break;
      case 'bottom':
        setCoords({ top: r.bottom + gap, left: r.left + r.width / 2 });
        break;
      case 'left':
        setCoords({ top: r.top + r.height / 2, left: r.left - gap });
        break;
      case 'right':
        setCoords({ top: r.top + r.height / 2, left: r.right + gap });
        break;
    }
  };

  const show = () => {
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => {
      computeCoords();
      setVisible(true);
    }, delay);
  };
  const hide = () => {
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    setVisible(false);
  };

  // Tooltip kutusunu hesaplanan ankor noktasına göre hizala (fixed → viewport koordinatları).
  const anchorClass = {
    top: '-translate-x-1/2 -translate-y-full',
    bottom: '-translate-x-1/2',
    left: '-translate-x-full -translate-y-1/2',
    right: '-translate-y-1/2',
  }[side];

  return (
    <span
      ref={triggerRef}
      className={cn('relative inline-flex', className)}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      <span aria-describedby={visible ? id : undefined} className="contents">
        {children}
      </span>
      {visible &&
        coords &&
        createPortal(
          <span
            id={id}
            role="tooltip"
            style={{ position: 'fixed', top: coords.top, left: coords.left }}
            className={cn(
              'pointer-events-none z-[3000] max-w-[240px] whitespace-pre-wrap rounded-custel-sm bg-custel-ink px-2.5 py-1.5 text-[12px] leading-[1.4] text-custel-krem shadow-custel-md',
              anchorClass,
            )}
          >
            {content}
          </span>,
          document.body,
        )}
    </span>
  );
}
